/**
 * セキュリティ機能の API（2段階認証・IP アドレス制限・端末証明書）。
 *   /api/auth/mfa/*                 本人の 2段階認証
 *   /api/auth/client-info           接続元 IP・端末証明書の状態
 *   /api/admin/ip-rules             IP アドレス制限（基金）
 *   /api/admin/device-certificates  端末証明書（基金）
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import type { AuthUser, OrganizationType } from '@/auth/types';

// ------------------------------------------------------------------ 2段階認証
export interface MfaStatus {
  loginId: string;
  name: string;
  /** 確認待ちセッションの状態（本セッションは null） */
  pending: 'verify' | 'setup' | null;
  required: boolean;
  enabled: boolean;
  enabledAt: string | null;
  recoveryCodesRemaining: number;
}

export interface MfaSetupResult {
  secret: string;
  otpauthUri: string;
  issuer: string;
  accountName: string;
}

export interface MfaEnableResult {
  recoveryCodes: string[];
  /** 確認待ち（setup）から有効化した場合はログインしたユーザー */
  user: AuthUser | null;
}

export const securityKeys = {
  mfa: ['security', 'mfa'] as const,
  clientInfo: ['security', 'client-info'] as const,
  ipRules: ['security', 'ip-rules'] as const,
  certs: ['security', 'device-certificates'] as const,
};

/** 2段階認証の API は 401 でもログイン画面へ自動遷移しない（確認待ちセッションで使うため） */
const noRedirect = { skipUnauthorizedRedirect: true } as const;

export const mfaApi = {
  status: () => api.get<MfaStatus>('/auth/mfa', undefined, noRedirect),
  verify: (body: { code: string } | { recoveryCode: string }) =>
    api.post<AuthUser & { recoveryCodesRemaining?: number }>('/auth/mfa/verify', body, noRedirect),
  setup: () => api.post<MfaSetupResult>('/auth/mfa/setup', undefined, noRedirect),
  enable: (code: string) => api.post<MfaEnableResult>('/auth/mfa/enable', { code }, noRedirect),
  disable: (password: string) => api.post<{ ok: true }>('/auth/mfa/disable', { password }),
  regenerate: (password: string) => api.post<{ recoveryCodes: string[] }>('/auth/mfa/recovery-codes', { password }),
};

export function useMfaStatus(enabled = true) {
  return useQuery({ queryKey: securityKeys.mfa, queryFn: mfaApi.status, enabled, staleTime: 0 });
}

export interface ClientInfo {
  ip: string | null;
  ipRestrictionDisabled: boolean;
  clientCertRequired: boolean;
  certificate: {
    presented: boolean;
    fingerprint: string | null;
    subject: string | null;
    registered: boolean;
    status: CertificateStatus | null;
    deviceName: string | null;
  };
}

export function useClientInfo() {
  return useQuery({
    queryKey: securityKeys.clientInfo,
    queryFn: () => api.get<ClientInfo>('/auth/client-info', undefined, noRedirect),
    staleTime: 60_000,
  });
}

// ------------------------------------------------------------------ IP アドレス制限
export interface IpRuleDto {
  id: number;
  organizationType: OrganizationType;
  businessOperatorId: number | null;
  businessOperatorName: string | null;
  cidr: string;
  note: string | null;
  isEnabled: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface IpRuleList {
  items: IpRuleDto[];
  summary: { organizationType: OrganizationType; businessOperatorId: number | null; total: number; enabled: number }[];
  currentIp: string | null;
  restrictionDisabled: boolean;
}

export interface IpRuleInput {
  organizationType: OrganizationType;
  businessOperatorId: number | null;
  cidr: string;
  note: string | null;
  isEnabled: boolean;
  confirmations?: string[];
}

export function useIpRules() {
  return useQuery({ queryKey: securityKeys.ipRules, queryFn: ({ signal }) => api.get<IpRuleList>('/admin/ip-rules', undefined, { signal }) });
}

export function useSaveIpRule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: number | null; input: IpRuleInput }) =>
      id == null ? api.post<IpRuleDto>('/admin/ip-rules', input) : api.patch<IpRuleDto>(`/admin/ip-rules/${id}`, input),
    onSuccess: () => qc.invalidateQueries({ queryKey: securityKeys.ipRules }),
  });
}

export function useDeleteIpRule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, confirmSelfLockout }: { id: number; confirmSelfLockout?: boolean }) =>
      api.delete(`/admin/ip-rules/${id}`, { query: confirmSelfLockout ? { confirm: 'selfLockout' } : undefined }),
    onSuccess: () => qc.invalidateQueries({ queryKey: securityKeys.ipRules }),
  });
}

// ------------------------------------------------------------------ 端末証明書
export type CertificateStatus = 'active' | 'revoked' | 'expired' | 'not_yet_valid';

export interface DeviceCertificateDto {
  id: number;
  fingerprintSha256: string;
  fingerprint: string;
  deviceName: string;
  subject: string | null;
  commonName: string | null;
  issuer: string | null;
  serialNumber: string | null;
  organizationType: OrganizationType | null;
  businessOperatorId: number | null;
  businessOperatorName: string | null;
  notBefore: string | null;
  notAfter: string;
  status: CertificateStatus;
  revokedAt: string | null;
  revokedReason: string | null;
  lastUsedAt: string | null;
  lastUsedIp: string | null;
  note: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface DeviceCertificateList {
  items: DeviceCertificateDto[];
  currentFingerprint: string | null;
  clientCertRequired: boolean;
}

export interface CertificatePreview {
  fingerprintSha256: string;
  fingerprint: string;
  subject: string;
  commonName: string | null;
  issuer: string;
  serialNumber: string;
  notBefore: string;
  notAfter: string;
  isCa: boolean;
  expired: boolean;
  duplicate: { id: number; deviceName: string } | null;
}

export interface DeviceCertificateInput {
  certificate: string;
  deviceName: string;
  organizationType: OrganizationType | null;
  businessOperatorId: number | null;
  note: string | null;
}

export function useDeviceCertificates() {
  return useQuery({
    queryKey: securityKeys.certs,
    queryFn: ({ signal }) => api.get<DeviceCertificateList>('/admin/device-certificates', undefined, { signal }),
  });
}

export const previewCertificate = (certificate: string) =>
  api.post<CertificatePreview>('/admin/device-certificates/preview', { certificate });

export function useRegisterCertificate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: DeviceCertificateInput) => api.post<DeviceCertificateDto>('/admin/device-certificates', input),
    onSuccess: () => qc.invalidateQueries({ queryKey: securityKeys.certs }),
  });
}

export function useUpdateCertificate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: number; input: Partial<Omit<DeviceCertificateInput, 'certificate'>> }) =>
      api.patch<DeviceCertificateDto>(`/admin/device-certificates/${id}`, input),
    onSuccess: () => qc.invalidateQueries({ queryKey: securityKeys.certs }),
  });
}

export function useRevokeCertificate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reason }: { id: number; reason: string }) =>
      api.post<DeviceCertificateDto>(`/admin/device-certificates/${id}/revoke`, { reason }),
    onSuccess: () => qc.invalidateQueries({ queryKey: securityKeys.certs }),
  });
}

export const CERT_STATUS_LABELS: Record<CertificateStatus, string> = {
  active: '有効',
  revoked: '失効',
  expired: '期限切れ',
  not_yet_valid: '有効期間前',
};
